/**
 * Export floor plan as PNG with a summary panel (metrics, AP list, dimensions).
 */

import type { FloorPlanMetrics, RequirementPassSummary } from './rfFloorMetrics'
import type { InterferencePlanStatus } from './rfInterferencePolicy'
import type { PlacedAp } from './types'

export type FloorReportOptions = {
  mapCanvas: HTMLCanvasElement
  floorName: string
  widthM: number
  heightM: number
  bandGHz: number
  metrics: FloorPlanMetrics | null
  requirementPass?: RequirementPassSummary | null
  interferencePlan?: InterferencePlanStatus | null
  aps: PlacedAp[]
  buildingName?: string
  floorLevel?: string
}

function safeFileName(name: string) {
  return name.replace(/[^\w.-]+/g, '_').slice(0, 48)
}

export function downloadFloorReport(opts: FloorReportOptions): void {
  const panelW = 280
  const mapW = opts.mapCanvas.width
  const mapH = opts.mapCanvas.height
  const out = document.createElement('canvas')
  out.width = mapW + panelW
  out.height = mapH
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')

  ctx.fillStyle = '#0f172a'
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.drawImage(opts.mapCanvas, 0, 0)

  const px = mapW + 16
  let y = 20
  ctx.fillStyle = '#f8fafc'
  ctx.font = '700 14px Inter, system-ui, sans-serif'
  ctx.fillText('RF Floor Plan Report', px, y)
  y += 22

  ctx.fillStyle = '#94a3b8'
  ctx.font = '500 11px Inter, system-ui, sans-serif'
  ctx.fillText(opts.floorName, px, y)
  y += 16
  if (opts.buildingName) {
    ctx.fillText(`Building: ${opts.buildingName}`, px, y)
    y += 14
  }
  if (opts.floorLevel) {
    ctx.fillText(`Level: ${opts.floorLevel}`, px, y)
    y += 14
  }
  ctx.fillText(`${opts.widthM}×${opts.heightM} m · ${opts.bandGHz} GHz`, px, y)
  y += 16

  if (opts.interferencePlan?.tuned) {
    ctx.fillStyle = '#34d399'
    ctx.font = '600 10px Inter, system-ui, sans-serif'
    ctx.fillText('RF PLAN: TUNED', px, y)
    y += 14
    ctx.font = '500 10px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#94a3b8'
    ctx.fillText(
      `Channels set · avg ${Math.round(opts.interferencePlan.avgTxDbm)} dBm · ~${opts.interferencePlan.expectedHighInterfPct}% overlap normal`,
      px,
      y,
    )
    y += 16
  }

  ctx.fillStyle = '#64748b'
  ctx.font = '600 10px Inter, system-ui, sans-serif'
  ctx.fillText('COVERAGE METRICS', px, y)
  y += 14

  ctx.font = '500 11px Inter, system-ui, sans-serif'
  if (opts.metrics) {
    const lines = [
      `Good (≥ −65 dBm): ${Math.round(opts.metrics.goodPct)}%`,
      `Weak (< −75 dBm): ${Math.round(opts.metrics.weakPct)}%`,
      `High interference: ${Math.round(opts.metrics.highInterfPct)}%`,
      `Avg RSSI: ${Math.round(opts.metrics.avgRssi)} dBm`,
      `Score: ${Math.round(opts.metrics.score)}`,
    ]
    ctx.fillStyle = '#cbd5e1'
    for (const line of lines) {
      ctx.fillText(line, px, y)
      y += 14
    }
  } else {
    ctx.fillStyle = '#64748b'
    ctx.fillText('No APs placed', px, y)
    y += 14
  }

  if (opts.requirementPass && opts.requirementPass.zones.length > 0) {
    y += 8
    ctx.fillStyle = '#64748b'
    ctx.font = '600 10px Inter, system-ui, sans-serif'
    ctx.fillText('REQUIREMENT ZONES', px, y)
    y += 14
    ctx.font = '500 11px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#cbd5e1'
    ctx.fillText(`Overall pass: ${Math.round(opts.requirementPass.overallPassPct)}%`, px, y)
    y += 14
    for (const z of opts.requirementPass.zones.slice(0, 6)) {
      const pctLabel = z.passPct == null ? 'n/a' : `${Math.round(z.passPct)}%`
      const status =
        z.passPct == null ? '·' : z.passPct >= 95 ? '✓' : z.passPct >= 80 ? '~' : '✗'
      ctx.fillText(`${status} ${z.name}: ${pctLabel}`, px, y)
      y += 13
    }
  }

  y += 8
  ctx.fillStyle = '#64748b'
  ctx.font = '600 10px Inter, system-ui, sans-serif'
  ctx.fillText(`ACCESS POINTS (${opts.aps.length})`, px, y)
  y += 14
  ctx.font = '500 10px Inter, system-ui, sans-serif'
  ctx.fillStyle = '#94a3b8'
  for (const ap of opts.aps.slice(0, 12)) {
    const tx = ap.txPowerDbm ?? 15
    ctx.fillText(`${ap.ap} · ${tx} dBm`, px, y)
    y += 12
  }
  if (opts.aps.length > 12) {
    ctx.fillText(`… +${opts.aps.length - 12} more`, px, y)
  }

  y = mapH - 12
  ctx.fillStyle = '#475569'
  ctx.font = '400 9px Inter, system-ui, sans-serif'
  ctx.fillText(`Generated ${new Date().toLocaleString()} · VVDN RF Floor Plan`, px, y)

  const url = out.toDataURL('image/png')
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeFileName(opts.floorName)}-report.png`
  a.click()
}
